import { AlertTriangle, CheckCircle2, Info, Lightbulb, ShieldAlert, X } from 'lucide-react';
import {
  createElement,
  forwardRef,
  isValidElement,
  type ComponentType,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/cn';

/**
 * Callout — a bounded, non-blocking message inside a page or a form.
 *
 * A tinted ground, a hairline, one hue per callout. Use it for context the user needs in
 * place; use a Toast for the result of an action and an ErrorState for a dead surface.
 */

export type CalloutVariant = 'info' | 'success' | 'warning' | 'danger' | 'accent' | 'neutral';

export interface CalloutProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: CalloutVariant;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ComponentType<{ className?: string; strokeWidth?: number }> | ReactNode | false;
  /** Trailing action, e.g. a ghost <Button>. */
  action?: ReactNode;
  onDismiss?: () => void;
  /** Single-line treatment with tighter padding. */
  compact?: boolean;
  className?: string;
}

/* A callout's edge carries the same semantic as its fill — a neutral hairline
   around a rose ground reads as a mistake and disappears against it. Spelled
   out per variant: Tailwind only sees class names it can read in the source. */

const VARIANT: Record<
  CalloutVariant,
  {
    ground: string;
    border: string;
    icon: string;
    mark: ComponentType<{ className?: string; strokeWidth?: number }>;
  }
> = {
  info: {
    ground: 'bg-[var(--info-soft)]',
    border: 'border-[color-mix(in_srgb,var(--info)_36%,transparent)]',
    icon: 'text-[var(--info)]',
    mark: Info,
  },
  success: {
    ground: 'bg-[var(--success-soft)]',
    border: 'border-[color-mix(in_srgb,var(--success)_36%,transparent)]',
    icon: 'text-[var(--success)]',
    mark: CheckCircle2,
  },
  warning: {
    ground: 'bg-[var(--warning-soft)]',
    border: 'border-[color-mix(in_srgb,var(--warning)_36%,transparent)]',
    icon: 'text-[var(--warning)]',
    mark: AlertTriangle,
  },
  danger: {
    ground: 'bg-[var(--danger-soft)]',
    border: 'border-[color-mix(in_srgb,var(--danger)_36%,transparent)]',
    icon: 'text-[var(--danger)]',
    mark: ShieldAlert,
  },
  accent: {
    ground: 'bg-[var(--accent-soft)]',
    border: 'border-accent-line',
    icon: 'text-accent-text',
    mark: Lightbulb,
  },
  neutral: { ground: 'bg-[var(--surface-2)]', border: 'border-line', icon: 'text-muted', mark: Info },
};

export const Callout = forwardRef<HTMLDivElement, CalloutProps>(function Callout(
  {
    variant = 'info',
    title,
    children,
    icon,
    action,
    onDismiss,
    compact = false,
    className,
    role,
    ...rest
  },
  ref,
) {
  const spec = VARIANT[variant];

  return (
    <div
      ref={ref}
      // The variant's own live-region semantic, unless the caller has a better
      // one — e.g. a message that is focused programmatically and so is read
      // on arrival without an alert.
      role={role ?? (variant === 'danger' || variant === 'warning' ? 'alert' : 'note')}
      className={cn(
        'flex items-start gap-3 rounded-[var(--radius-md)] border',
        spec.ground,
        spec.border,
        // The compact box declares its own height beside its own padding, so a
        // caller reserving space for one can ask for `--h-callout-compact`
        // rather than measuring this component and writing the number down.
        compact ? 'min-h-[var(--h-callout-compact)] px-3 py-2' : 'p-3',
        className,
      )}
      {...rest}
    >
      {icon === false ? null : (
        <span className={cn('mt-px flex size-4 shrink-0 items-center justify-center', spec.icon)}>
          <CalloutIcon icon={icon} fallback={spec.mark} />
        </span>
      )}

      <div className="min-w-0 flex-1">
        {title ? (
          <p className="text-base font-medium leading-snug text-primary">{title}</p>
        ) : null}
        {children ? (
          <div
            className={cn(
              'text-base leading-normal text-secondary',
              title && 'mt-1',
              compact && 'text-sm leading-snug',
            )}
          >
            {children}
          </div>
        ) : null}
      </div>

      {action ? <div className="shrink-0">{action}</div> : null}

      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss message"
          className={cn(
            'mt-px flex size-[var(--h-control-sm)] shrink-0 items-center justify-center',
            '-mr-1 rounded-[var(--radius-sm)] text-muted transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
            'hover:bg-[var(--surface-hover)] hover:text-primary',
            'active:bg-[var(--surface-active)]',
            'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
          )}
        >
          <X className="size-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
});

function CalloutIcon({
  icon,
  fallback,
}: {
  icon?: ComponentType<{ className?: string; strokeWidth?: number }> | ReactNode | false;
  fallback: ComponentType<{ className?: string; strokeWidth?: number }>;
}) {
  if (typeof icon === 'function') {
    return createElement(icon, { className: 'size-4', strokeWidth: 1.75 });
  }
  if (icon && isValidElement(icon)) return <>{icon}</>;
  return createElement(fallback, { className: 'size-4', strokeWidth: 1.75 });
}
